import type { Metadata } from "next";

export const SITE_URL = "https://clearcurve.org";
export const SOCIAL_IMAGE_VERSION = "20260610";
export const SOCIAL_IMAGE_ALT = "ClearCurve PSX market intelligence dashboard";
export const DEFAULT_OG_IMAGE_PATH = `/opengraph-image?v=${SOCIAL_IMAGE_VERSION}`;
export const DEFAULT_TWITTER_IMAGE_PATH = `/twitter-image?v=${SOCIAL_IMAGE_VERSION}`;

type MetadataInput = {
  title: string;
  description: string;
  path?: string;
  keywords?: Metadata["keywords"];
  type?: "website" | "article";
  noIndex?: boolean;
};

export function absoluteUrl(path = "/") {
  return new URL(path, SITE_URL).toString();
}

export function buildMetadata({
  title,
  description,
  path,
  keywords,
  type = "website",
  noIndex = false,
}: MetadataInput): Metadata {
  const url = path ? absoluteUrl(path) : SITE_URL;
  return {
    title,
    description,
    keywords,
    alternates: {
      canonical: path,
      languages: {
        en: url,
        "en-PK": url,
        "x-default": url,
      },
    },
    robots: noIndex ? { index: false, follow: true } : undefined,
    openGraph: {
      title,
      description,
      url,
      siteName: "ClearCurve",
      type,
      images: [
        {
          url: absoluteUrl(DEFAULT_OG_IMAGE_PATH),
          width: 1200,
          height: 630,
          alt: SOCIAL_IMAGE_ALT,
        },
      ],
    },
    twitter: {
      card: "summary_large_image",
      title,
      description,
      images: [absoluteUrl(DEFAULT_TWITTER_IMAGE_PATH)],
    },
  };
}

export function buildBreadcrumbJsonLd(items: Array<{ name: string; path: string }>) {
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: absoluteUrl(item.path),
    })),
  };
}

export const organizationJsonLd = {
  "@context": "https://schema.org",
  "@type": "Organization",
  name: "ClearCurve",
  url: SITE_URL,
  logo: absoluteUrl("/branding/logo-icon.svg"),
  email: "clearcurve1@gmail.com",
  description:
    "ClearCurve provides a Pakistan Stock Exchange research workspace for PSX scanners, participant flow (FIPI/LIPI), money flow, settlement confirmation, and institutional positioning.",
  areaServed: {
    "@type": "Country",
    name: "Pakistan",
  },
  contactPoint: [
    {
      "@type": "ContactPoint",
      contactType: "customer support",
      email: "clearcurve1@gmail.com",
      availableLanguage: ["English"],
    },
  ],
};

export const websiteJsonLd = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  name: "ClearCurve",
  url: SITE_URL,
  inLanguage: "en-PK",
  description:
    "Pakistan Stock Exchange research workspace for PSX scanners, FIPI/LIPI flow, money flow, settlement confirmation, and market analysis.",
  potentialAction: {
    "@type": "SearchAction",
    target: {
      "@type": "EntryPoint",
      urlTemplate: `${SITE_URL}/search?q={search_term_string}`,
    },
    "query-input": "required name=search_term_string",
  },
};

export const softwareApplicationJsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "ClearCurve",
  applicationCategory: "FinanceApplication",
  operatingSystem: "Web",
  url: SITE_URL,
  description:
    "Pakistan Stock Exchange research workspace for PSX scanners, participant flow (FIPI/LIPI), money flow, settlement confirmation, institutional positioning, and PSX index dashboards.",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "PKR",
  },
  featureList: [
    "PSX Scanner — RSI, MACD, EMA, Renko, Elliott Wave, Minervini VCP",
    "Participant Flow — FIPI and LIPI capital tracking",
    "Settlement Confirmation — UIN-level verification",
    "Money Flow Analysis — sector absorption and pressure",
    "Money Flow Tracking — off-market and block deals",
    "PSX Index Dashboards — KSE-100, KMI-30, KSE All Shares, MII30, PSXDIV20",
    "PSX Strategy Workbench — setup playbooks and backtest snapshots",
    "PSX Learning Center — beginner to advanced guides",
  ],
  inLanguage: "en-PK",
  audience: {
    "@type": "Audience",
    name: "Pakistan Stock Exchange Traders and Investors",
  },
};
